// Roles & access: labels for the access policies and permissions a brand's
// roles carry (Super Admin > Roles & access), and how they map onto pages.
// Keys mirror ACCESS_POLICIES / ROLE_PERMISSIONS in apps/api/schema_map.py,
// which enforces them. Rendering aid only — the API is the boundary.

import { IDENTITY_CONTROL, ROLE_VIEWS } from "./roleViews";

export const ACCESS_POLICIES = [
  { id: "entire_brand", label: "Entire brand" },
  { id: "selected_regions", label: "Selected regions" },
  { id: "one_region", label: "One region" },
  { id: "selected_dealers", label: "Selected dealerships" },
  { id: "selected_workshops", label: "Selected workshops" },
  { id: "one_workshop", label: "One workshop" },
  { id: "assigned_customers", label: "Assigned customers only" },
];

// One per page the product still has. `group` is only how the Roles editor
// lays them out — each is its own grant, ticked on its own.
export const PERMISSIONS = [
  { id: "advisors_desk", label: "Advisor's Desk" },
  { id: "chat_audit", label: "Chat Retrieval / Audit" },
  { id: "book_service", label: "Book Service" },
  { id: "workshop_analytics", label: "Workshop Analytics" },
  { id: "system_health", label: "System health" },
  { id: "receive_notifications", label: "Receive notifications" },
  { id: "manage_regions", label: "Manage Regions", group: "Organization", levelled: true },
  { id: "manage_dealers", label: "Manage Dealers", group: "Organization", levelled: true },
  { id: "manage_workshops", label: "Manage Workshops", group: "Organization", levelled: true },
  { id: "manage_roles", label: "Manage Roles & Access", levelled: true },
  { id: "manage_people", label: "Manage People", levelled: true },
];

// A levelled permission is held as the bare key (edit) or the key plus this
// suffix (read). Edit implies read, so never both. Mirrors READ_SUFFIX in
// apps/api/schema_map.py.
export const READ_SUFFIX = ":read";

/** "edit", "read" or false for one capability. */
export function levelOf(permissions, id) {
  const held = permissions || [];
  if (held.includes(id)) return "edit";
  if (held.includes(id + READ_SUFFIX)) return "read";
  return false;
}

// Escalations and complaints are not a grant of their own — whoever works the
// queue or measures it already owns them. Mirrors COMPLAINTS_FROM in
// apps/api/schema_map.py.
export const COMPLAINTS_FROM = ["workshop_analytics", "advisors_desk"];

export const policyLabel = (id) => ACCESS_POLICIES.find((p) => p.id === id)?.label || id || "";

// Policies whose login may be given a region — mirrors REGION_SCOPE_POLICIES
// in apps/api/server.py.
export const REGION_SCOPE_POLICIES = new Set(["one_region", "selected_regions", "selected_workshops"]);

// The page map a role's permissions open, in ROLE_VIEWS' shape.
export function pagesFor(permissions) {
  const has = (p) => permissions.includes(p);
  // Overview and Service Journey are being retired, so nothing grants them.
  // Their routes still resolve for anyone holding a link; they are simply no
  // longer offered.
  return {
    overview: false,
    notifications: false,
    jobcards: false,
    journey: false,
    chatHistory: has("chat_audit"),
    create: has("book_service"),
    desk: has("advisors_desk"),
    analytics: has("workshop_analytics"),
    systemHealth: has("system_health"),
    // The Pre-IFR Agent follows the analytics and desk grants: whoever measures or works the queue.
    preIfr: has("workshop_analytics") || has("advisors_desk"),
    // The five levelled ones answer "edit" | "read" | false, so a page can be
    // shown without its write controls.
    stakeholders: levelOf(permissions, "manage_workshops"),
    users: levelOf(permissions, "manage_people"),
    complaints: COMPLAINTS_FROM.some(has) ? "manage" : "read",
    regions: levelOf(permissions, "manage_regions"),
    dealers: levelOf(permissions, "manage_dealers"),
    workshops: levelOf(permissions, "manage_workshops"),
    people: levelOf(permissions, "manage_people"),
    roles: levelOf(permissions, "manage_roles"),
  };
}

// Which demo-switcher controls a policy needs.
export function identityControlFor(policy, role) {
  if (policy === "entire_brand") return { locationControl: "hidden", needsAdvisor: false };
  if (policy === "assigned_customers") return { locationControl: "single", needsAdvisor: true };
  if (policy === "one_workshop" || policy === "one_region") return { locationControl: "single", needsAdvisor: false };
  if (policy) return { locationControl: "multi", needsAdvisor: false };
  return IDENTITY_CONTROL[role] || { locationControl: "single", needsAdvisor: false };
}

// Whether `role` may open `page`, from the brand's role rows when it has one
// for this role; the built-in map covers logins before roles load.
export function canSeePage(role, page, roleRows) {
  if (role === "master") return true;
  const row = (roleRows || []).find((r) => r.code === role);
  if (row) return !!pagesFor(row.permissions || [])[page];
  return !!ROLE_VIEWS[role]?.pages?.[page];
}
